import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface CreateWorkspaceParams {
  name: string;
  plan_code?: string;
}

interface CreateWorkspaceResponse {
  workspace: {
    id: string;
    name: string;
    slug: string;
    plan_id: string;
    created_at: string;
    plan: {
      code: string;
      name: string;
    };
  };
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (params: CreateWorkspaceParams): Promise<CreateWorkspaceResponse> => {
      console.log('[useCreateWorkspace] Creating workspace:', params);
      
      if (!params.name || params.name.trim().length === 0) {
        throw new Error('Workspace name is required');
      }

      const url = `https://dyftqspuiyabqzjslwyw.supabase.co/functions/v1/workspace-create`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR5ZnRxc3B1aXlhYnF6anNsd3l3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM0MjA5MzIsImV4cCI6MjA2ODk5NjkzMn0.mibvMCccmDDhYCg7JY42pyc9dXjnYWWE32lVrv25V-U',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: params.name.trim(),
          plan_code: params.plan_code || 'trial'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[useCreateWorkspace] Error:', errorData);
        throw new Error(errorData.error || 'Failed to create workspace');
      }

      const data = await response.json();

      if (!data || !data.workspace) {
        throw new Error('Invalid response from workspace creation API');
      }

      console.log('[useCreateWorkspace] Success:', data.workspace.id);
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Workspace "${data.workspace.name}" created successfully!`);
      
      // Invalidate queries to refresh workspace lists
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-modules'] });
      
      // Navigate to the new workspace
      navigate(`/w/${data.workspace.id}/dashboard`);
    },
    onError: (error: Error) => {
      console.error('[useCreateWorkspace] Mutation error:', error);
      toast.error(error.message || 'Failed to create workspace');
    }
  });
}